import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ExclamationOutlined } from '@ant-design/icons';
import { FolderOpenOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Dropdown, Modal, Row, Tabs } from 'antd';
import { isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import BuyAQuoteModal from './BuyAQuoteModal';
import LPCarrierStatus from './LPCarrierStatus';
import ShipmentMapWrapper from './map/ShipmentMapWrapper';
import { ConfirmCancelReturnToSenderModal } from './returnToSender/ConfirmCancelReturnToSenderModal';
import { ConfirmReturnToSenderModal } from './returnToSender/ConfirmReturnToSenderModal';
import ShipmentDetailsForm from './ShipmentDetailsForm';
import ShipmentELabel from './ShipmentELabel';
import ShipmentForm from './ShipmentForm';
import ShipmentStateEventHistory from './ShipmentStateEventHistory';
import TrackingCodeForm from './TrackingCodeForm';
import BoxFirmwareHistory from '../../boxes/components/BoxFirmwareHistory';
import useBoxFirmwareByDates from '../../boxes/hooks/useBoxFirmareByDates';
import useServiceLevelsStore from '../../serviceLevels/hooks/useServiceLevelsStore';
import useEditShipment from '../hooks/useEditShipment';
import useGetShipment from '../hooks/useGetShipment';
import { DeliveryStatus } from '../types/deliveryStatus';
import BillingManager from '@/components/billing/BillingManager';
import TabPane from '@/components/layout/TabPane';
import PillButton from '@/components/PillButton';
import ScopedCta from '@/components/ScopedCta';
import H2 from '@/components/text/H2';
import { scopes } from '@/config';
import { BoxTelemetryEvents } from '@/features/boxes';
import { useCreateLitigation } from '@/features/gdpr/litigation/api/createLitigation';
import { useExportShipmentHandler } from '@/features/gdpr/litigation/api/exportShipmentData';
import { useUpdateLitigation } from '@/features/gdpr/litigation/api/updateLitigation';
import { LitigationModal } from '@/features/gdpr/litigation/components/LitigationModal';
import { getDropdownItems } from '@/features/gdpr/litigation/hooks/getDropdownItems';
import {
  LitigationAction,
  LitigationStatus,
  LitigationFormData,
} from '@/features/gdpr/litigation/types';
import { useUpdateStatusRTS } from '@/features/shipments/api/useUpdateStatusRTS';
import { SecurityInspection } from '@/features/shipments/components/securityInspection/SecurityInspection';
import useAppState from '@/hooks/useAppState';
import useError from '@/hooks/useError';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

const ELABEL_STATES = [
  'PURCHASED',
  'DEPLOYED',
  'ARRIVED',
  'TRANSFERRED',
  'CANCELLED',
];

const StyledSpan = styled.span`
  opacity: 0.7;
`;
const { confirm } = Modal;

const ShipmentDetailsContent = ({
  setIsRTS,
  litigationState,
  litigationId,
}: {
  setIsRTS: Dispatch<SetStateAction<boolean>>;
  litigationState: LitigationStatus | null;
  litigationId: string;
}) => {
  const addError = useError();
  const [tab, setTab] = useState('details');
  const [openModal, setOpenModal] = useState<boolean>(false);

  const { id } = useParams<{ id: string }>();

  const { mutate: updateLitigation } = useUpdateLitigation({ shipmentId: id! });
  const { mutate: createLitigation } = useCreateLitigation({ shipmentId: id! });

  const { mutateAsync: updateStatusRTS } = useUpdateStatusRTS();

  const handleExport = useExportShipmentHandler();
  const userScopes = useAppState((state) => state.scopes);
  const addSuccessMsg = useToasts((state) => state.addItem);
  const [shipment, getShipment] = useGetShipment(id);
  const [dropdownValue, setDropdownValue] = useState<LitigationAction | null>(
    null
  );
  const [isSecurityInspectionOpen, setIsSecurityInspectionOpen] =
    useState<boolean>(false);
  const serviceLevelsList = useServiceLevelsStore(
    (state) => state.serviceLevels
  );
  const [confirmRTSModalOpen, setConfirmRTSModalOpen] = useState(false);

  const {
    deleteShipment,
    archiveShipment,
    unArchiveShipment,
    restoreShipment,
    updateBilling,
  } = useEditShipment();

  const hasElabel = ELABEL_STATES.includes(shipment.contractState);
  const canChangeStatus =
    shipment.contractState !== 'DRAFT' && shipment.carrierCode === 'LPCarrier';
  const canBuyQuote =
    shipment.quote?.id === '' && shipment.contractState === 'DRAFT';
  const canArchive =
    (shipment.contractState === 'ARRIVED' && !shipment.isArchived) ||
    (shipment.contractState === 'CANCELLED' && !shipment.isArchived);
  const canUnarchive =
    (shipment.contractState === 'ARRIVED' && shipment.isArchived) ||
    (shipment.contractState === 'CANCELLED' && shipment.isArchived);
  const canDelete =
    ((shipment.contractState === 'DRAFT' ||
      shipment.contractState === 'PURCHASED') &&
      shipment.deletedAt === 0) ||
    ((shipment.contractState === 'DRAFT' ||
      shipment.contractState === 'PURCHASED') &&
      !shipment.deletedAt);
  const canRestore =
    (shipment.contractState === 'DRAFT' ||
      shipment.contractState === 'PURCHASED') &&
    shipment.deletedAt &&
    shipment.deletedAt !== 0;

  //Replace id by name of the service level
  serviceLevelsList.forEach((srvLvl) => {
    if (shipment.serviceLevel === srvLvl.id) {
      shipment.serviceLevel = srvLvl.name;
    }
  });

  const handleArchive = useCallback(async () => {
    await archiveShipment(shipment.id);
    getShipment();
  }, [shipment, archiveShipment, getShipment]);

  const handleUnArchive = useCallback(async () => {
    await unArchiveShipment(shipment.id);
    getShipment();
  }, [shipment, unArchiveShipment, getShipment]);

  const handleDelete = useCallback(async () => {
    await deleteShipment(shipment.id);
    getShipment();
  }, [shipment, deleteShipment, getShipment]);

  const handleRestore = useCallback(async () => {
    await restoreShipment(shipment.id);
    getShipment();
  }, [shipment, restoreShipment, getShipment]);

  const showConfirmArchive = useCallback(() => {
    confirm({
      okText: 'Yes',
      title: 'Archive Shipment',
      icon: <ExclamationOutlined />,
      content: 'Are you sure you want to archive this shipment?',
      onOk() {
        handleArchive();
      },
      onCancel() {},
    });
  }, [handleArchive]);

  const showConfirmUnArchive = useCallback(() => {
    confirm({
      okText: 'Yes',
      title: 'Unarchive Shipment',
      icon: <ExclamationOutlined />,
      content: 'Are you sure you want to unarchive this shipment?',
      onOk() {
        handleUnArchive();
      },
      onCancel() {},
    });
  }, [handleUnArchive]);

  const showConfirmDelete = useCallback(() => {
    confirm({
      okText: 'Yes',
      title: 'Delete Shipment',
      icon: <ExclamationOutlined />,
      content: 'Are you sure you want to delete this shipment?',
      onOk() {
        handleDelete();
      },
      onCancel() {},
    });
  }, [handleDelete]);

  const showConfirmRestore = useCallback(() => {
    confirm({
      okText: 'Yes',
      title: 'Restore Shipment',
      icon: <ExclamationOutlined />,
      content: 'Are you sure you want to restore this shipment?',
      onOk() {
        handleRestore();
      },
      onCancel() {},
    });
  }, [handleRestore]);

  const showLink = useMemo(() => {
    if (shipment.returnShipmentId || shipment.originalShipmentId) {
      return shipment.returnShipmentId
        ? shipment.returnShipmentId
        : shipment.originalShipmentId;
    }

    return false;
  }, [shipment]);

  const setBillable = useCallback(
    async (body) => {
      await updateBilling(shipment.id, body);
      getShipment();
    },
    [updateBilling, getShipment, shipment]
  );

  const status = useCallback(() => {
    let status: string = shipment.contractState;

    if (shipment.isArchived) {
      status =
        shipment.contractState === 'ARRIVED' ||
        shipment.contractState === 'CANCELLED'
          ? 'ARCHIVED'
          : shipment.contractState;
    }
    if (shipment.deletedAt > 0) {
      status = 'DELETED';
    }

    return status;
  }, [shipment.contractState, shipment.isArchived, shipment.deletedAt]);

  const { firmwareClosestToDate } = useBoxFirmwareByDates(
    shipment.boxId,
    shipment.deployedAt
  );

  const isRTS = shipment.deliveryStatus === DeliveryStatus.RETURN_TO_SENDER;

  const handleClickUpdateStatusRTS = async () => {
    const { error, success } = await updateStatusRTS({
      shipmentId: shipment.id,
      status: !isRTS,
    });
    if (success) {
      addSuccessMsg(
        {
          msg: `Successfully send ${isRTS ? '"Cancel"' : ''} request to Return to Sender`,
          type: MessageType.Success,
        },
        'root',
        {}
      );
      // Wait for a bit to let BE process the status change
      await new Promise((resolve) => setTimeout(resolve, 500));
      getShipment();
    }

    if (error) {
      addError(error, { parseBeErrorMsg: true });
    }
  };

  const handleClickSecurityInspection = () => {
    setIsSecurityInspectionOpen(true);
  };

  const handleCloseSecurityInspection = () => {
    setIsSecurityInspectionOpen(false);
  };

  useEffect(() => {
    if (shipment && setIsRTS) setIsRTS(isRTS);
  }, [shipment, isRTS, setIsRTS]);

  const items = getDropdownItems(handleExport, id!, litigationState, (key) => {
    setDropdownValue(key);
    setOpenModal(true);
  });

  const handleSubmit = (data: LitigationFormData) => {
    try {
      const shouldCreate = data.status === 'FLAGGED';

      if (shouldCreate) {
        createLitigation({ litigationData: data });
      } else {
        updateLitigation({
          litigationData: data,
          litigationId,
        });
      }
    } catch (error) {
      addError(error);
    }

    setOpenModal(false);
  };

  const handleCancel = () => {
    setOpenModal(false);
  };

  return (
    <>
      <LitigationModal
        open={openModal}
        handleSubmit={handleSubmit}
        loading={false}
        handleCancel={handleCancel}
        litigationAction={dropdownValue}
        shipmentId={id!}
      />
      <Row gutter={[24, 24]}>
        <Col>
          <H2>
            <StyledSpan>STATE:</StyledSpan> &nbsp;
            {status()}
          </H2>
        </Col>
        {firmwareClosestToDate && (
          <Col>
            <H2>
              <StyledSpan>Firmware shipped:</StyledSpan> &nbsp;
              {firmwareClosestToDate}
            </H2>
          </Col>
        )}
        {shipment.reference && (
          <Col>
            <H2>
              <StyledSpan>Shipment name:</StyledSpan> &nbsp;
              {shipment.reference}
            </H2>
          </Col>
        )}
      </Row>
      <Row style={{ paddingTop: '15px', paddingBottom: '15px' }}>
        <Col flex="auto">
          {userScopes.includes(scopes.MANAGE_BILLING) && (
            <BillingManager shipment={shipment} onUpdate={setBillable} />
          )}
        </Col>
        <Col flex="auto">
          <Row gutter={[24, 24]} justify="end">
            {canArchive && (
              <Col>
                <ScopedCta
                  component={PillButton}
                  icon={<FolderOpenOutlined />}
                  type="default"
                  onClick={showConfirmArchive}
                  danger
                  requiredScopes={[scopes.MANAGE_SHIPMENTS]}
                >
                  Archive Shipment
                </ScopedCta>
              </Col>
            )}
            {canUnarchive && (
              <Col>
                <ScopedCta
                  component={PillButton}
                  icon={<FolderOpenOutlined />}
                  type="default"
                  onClick={showConfirmUnArchive}
                  danger
                  requiredScopes={[scopes.MANAGE_SHIPMENTS]}
                >
                  Unarchive Shipment
                </ScopedCta>
              </Col>
            )}
            {canDelete && (
              <Col>
                <ScopedCta
                  component={PillButton}
                  icon={<FolderOpenOutlined />}
                  type="default"
                  onClick={showConfirmDelete}
                  danger
                  requiredScopes={[scopes.MANAGE_SHIPMENTS]}
                >
                  Delete Shipment
                </ScopedCta>
              </Col>
            )}
            {!!canRestore && (
              <Col>
                <ScopedCta
                  component={PillButton}
                  icon={<FolderOpenOutlined />}
                  type="default"
                  onClick={showConfirmRestore}
                  danger
                  requiredScopes={[scopes.MANAGE_SHIPMENTS]}
                >
                  Restore Shipment
                </ScopedCta>
              </Col>
            )}
            {canBuyQuote && (
              <Col>
                <BuyAQuoteModal shipment={shipment} getShipment={getShipment} />
              </Col>
            )}
            {showLink && (
              <Col>
                <PillButton>
                  <Link to={`/shipments/${showLink}`}>
                    Go to {shipment.returnShipmentId ? 'return' : 'initial'}{' '}
                    shipment
                  </Link>
                </PillButton>
              </Col>
            )}
            {shipment.contractState === 'DEPLOYED' && (
              <>
                {isRTS ? (
                  <Col>
                    <ScopedCta
                      component={PillButton}
                      type="default"
                      onClick={() => setConfirmRTSModalOpen(true)}
                      danger
                      requiredScopes={[scopes.MANAGE_SHIPMENTS]}
                    >
                      Cancel Return to Sender
                    </ScopedCta>
                    <ConfirmCancelReturnToSenderModal
                      confirmRTSModalOpen={confirmRTSModalOpen}
                      setConfirmRTSModalOpen={setConfirmRTSModalOpen}
                      handleClickUpdateStatusRTS={handleClickUpdateStatusRTS}
                    />
                  </Col>
                ) : (
                  <Col>
                    <ScopedCta
                      component={PillButton}
                      type="default"
                      onClick={() => setConfirmRTSModalOpen(true)}
                      variant="outlined"
                      color="#38A169"
                      requiredScopes={[scopes.MANAGE_SHIPMENTS]}
                    >
                      Reflag as Return to Sender
                    </ScopedCta>
                    <ConfirmReturnToSenderModal
                      confirmRTSModalOpen={confirmRTSModalOpen}
                      setConfirmRTSModalOpen={setConfirmRTSModalOpen}
                      handleClickUpdateStatusRTS={handleClickUpdateStatusRTS}
                    />
                  </Col>
                )}
              </>
            )}
            {/* Only allow security inspection for deployed shipments */}
            {!isMobile && shipment.contractState === 'DEPLOYED' && (
              <Col>
                <ScopedCta
                  component={PillButton}
                  type="default"
                  onClick={handleClickSecurityInspection}
                  requiredScopes={[
                    scopes.MANAGE_SHIPMENTS,
                    scopes.MANAGE_PRODUCT,
                  ]}
                  data-testid="security-inspection-btn"
                >
                  Security inspection
                </ScopedCta>
              </Col>
            )}
            <Col>
              <Dropdown
                menu={{ items }}
                placement="bottomRight"
                arrow
                trigger={['click']}
              >
                <Button
                  data-testid="litigation-dropdown"
                  style={{ borderRadius: '2.375rem' }}
                >
                  <span style={{ paddingBottom: '.4375rem' }}>...</span>
                </Button>
              </Dropdown>
            </Col>
          </Row>
        </Col>
      </Row>

      <ShipmentForm shipment={shipment} />
      <Divider />
      <Tabs
        type="card"
        activeKey={tab}
        onTabClick={(key) => setTab(key)}
        data-testid="shipment-tabs"
      >
        <TabPane
          remount
          tab="Details"
          key="details"
          identifier="details"
          currentTab={tab}
        >
          <ShipmentDetailsForm shipment={shipment} />
        </TabPane>
        <TabPane remount tab="Map" key="map" identifier="map" currentTab={tab}>
          {shipment.id && <ShipmentMapWrapper shipment={shipment} />}
        </TabPane>
        <TabPane
          remount
          tab="Delivery Key"
          key="trackingCode"
          identifier="trackingCode"
          currentTab={tab}
        >
          {id && (
            <TrackingCodeForm
              id={id}
              contractStateStatus={
                shipment.contractState !== 'PURCHASED' ||
                shipment.contractState !== 'TRANSFERRED'
              }
              openingCode={shipment.openingCode}
              partnerId={shipment.partnerId}
            />
          )}
        </TabPane>
        <TabPane
          remount
          tab="LPCarrier"
          key="lpCarrier"
          identifier="lpCarrier"
          disabled={!canChangeStatus}
          currentTab={tab}
        >
          <LPCarrierStatus shipmentId={id} />
        </TabPane>
        <TabPane
          remount
          tab="Telemetry Events"
          key="telemetry"
          identifier="telemetry"
          disabled={!shipment.boxId}
          currentTab={tab}
        >
          <BoxTelemetryEvents
            isShipment
            id={id}
            lpUI={shipment.box.thingName}
          />
        </TabPane>
        <TabPane
          remount
          tab="Event History"
          key="eventHistory"
          identifier="eventHistory"
          currentTab={tab}
        >
          <ShipmentStateEventHistory id={id} />
        </TabPane>
        <TabPane
          tab="ELabel"
          key="eLabel"
          identifier="eLabel"
          disabled={!hasElabel}
          currentTab={tab}
        >
          {/* eLabel is not conditionally rendered to avoid requesting data on every tab switch TODO: pass tab prop to comp */}
          <ShipmentELabel shipment={shipment} />
        </TabPane>
        <TabPane
          remount
          tab="Firmware"
          key="firmware"
          identifier="firmware"
          currentTab={tab}
          disabled={!shipment?.boxId}
        >
          <BoxFirmwareHistory id={shipment?.boxId} />
        </TabPane>
      </Tabs>
      {isSecurityInspectionOpen && (
        <SecurityInspection
          open={isSecurityInspectionOpen}
          onClose={handleCloseSecurityInspection}
          lpui={shipment.box.thingName}
        />
      )}
    </>
  );
};
export default ShipmentDetailsContent;
